import {
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import styled from 'styled-components';
import {getColorFromTheme} from '@ui/ui-utils';

export type FeaturePageWidth = 'focused' | 'standard' | 'wide' | 'fluid';

export const FEATURE_PAGE_WIDTHS: Record<
  Exclude<FeaturePageWidth, 'fluid'>,
  number
> = {
  focused: 960,
  standard: 1120,
  wide: 1280,
};

const shellMaxWidth = (width: FeaturePageWidth) =>
  width === 'fluid' ? 'none' : `${FEATURE_PAGE_WIDTHS[width] + 64}px`;

export interface FeatureHeaderTab {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
  hidden?: boolean;
  disabled?: boolean;
}

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 3;
  height: 66px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundLight')};
`;

const HeaderInner = styled.div<{$width: FeaturePageWidth}>`
  display: flex;
  width: 100%;
  max-width: ${({$width}) => shellMaxWidth($width)};
  height: 66px;
  align-items: flex-end;
  justify-content: space-between;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Tabs = styled.nav`
  display: flex;
  min-width: 0;
  align-items: flex-end;
  gap: 32px;
  overflow-x: auto;
`;

const Tab = styled.button<{$active: boolean}>`
  position: relative;
  flex: 0 0 auto;
  padding: 0 0 18px;
  color: ${({$active, theme}) =>
    $active ? theme.colors.blackTwo : theme.colors.textBody};
  font-size: 14px;
  font-weight: ${({$active}) => ($active ? 600 : 500)};
  line-height: 22px;

  &::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    border-radius: 10px;
    background: ${({$active, theme}) =>
      $active ? theme.colors.purple300 : 'transparent'};
    content: '';
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 3px;
  }

  &:disabled {
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 8px;
`;

export const FeaturePageHeader = ({
  tabs,
  actions,
  ariaLabel,
  width = 'standard',
}: {
  tabs: FeatureHeaderTab[];
  actions?: ReactNode;
  ariaLabel: string;
  width?: FeaturePageWidth;
}) => {
  const tabsRef = useRef<HTMLElement>(null);
  const activeTabId = tabs.find((tab) => tab.active && !tab.hidden)?.id;

  useEffect(() => {
    const nav = tabsRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
    const navRect = nav.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    nav.scrollLeft +=
      activeRect.left -
      navRect.left -
      Math.max(0, (nav.clientWidth - activeRect.width) / 2);
  }, [activeTabId]);

  return (
    <Header>
      <HeaderInner
        $width={width}
        data-page-width={width}
        data-testid="feature-page-header-inner">
        <Tabs ref={tabsRef} aria-label={ariaLabel}>
          {tabs
            .filter(({hidden}) => !hidden)
            .map((tab) => (
              <Tab
                key={tab.id}
                type="button"
                $active={tab.active}
                disabled={tab.disabled}
                aria-current={tab.active ? 'page' : undefined}
                onClick={tab.onClick}>
                {tab.label}
              </Tab>
            ))}
        </Tabs>
        {actions && <Actions>{actions}</Actions>}
      </HeaderInner>
    </Header>
  );
};

const Content = styled.div<{$width: FeaturePageWidth}>`
  width: 100%;
  max-width: ${({$width}) => shellMaxWidth($width)};
  box-sizing: border-box;
  margin: 0 auto;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

export const FeaturePageContent = ({
  children,
  width = 'standard',
  ...props
}: PropsWithChildren<
  {width?: FeaturePageWidth} & HTMLAttributes<HTMLDivElement>
>) => (
  <Content
    $width={width}
    data-page-width={width}
    data-testid="feature-page-content"
    {...props}>
    {children}
  </Content>
);
